import GeneralAccordion from '~/components/Content/Accordion/GeneralAccordion'
import AudioSingle from '../components/Player'
import { useCallback, useState } from 'react'
import { isDevelopment } from '~/utils/misc'
import { useQuery } from '@tanstack/react-query'
import { getEntryById } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

interface PlayerProps {
  content: {
    isStandalone?: boolean
    title?: string
    tag?: string
    description?: string
    music: string
    artwork?: string
    showArtwork: boolean
    colour: string
    align?: 'top' | 'centre' | 'bottom' | undefined
    strongColour?: boolean
    showSeriesName?: boolean
    audio: {
      audioFile: { sys: { uri: string } }
      heading?: string
      copy?: string
      series?: { entryTitle: string }
      coverArt?: { asset: { sys: { uri: string } } }
      sys: { id: string }
    }
  }
  layoutColour?: string
}

const AudioStandalone = ({ content, layoutColour }: PlayerProps) => {
  const { strongColour, showSeriesName, audio } = content

  const { data, isLoading, error } = useQuery({
    queryKey: ['audio-entry', audio?.sys?.id],
    queryFn: async () => getEntryById(audio?.sys?.id || ''),
    enabled: !!audio?.sys?.id
  })

  const [playing, setPlaying] = useState(false)

  const handleTrackFinish = useCallback(() => {
    setPlaying(false) // Stop playing if it's the first track again
  }, [])

  const handlePlayPause = useCallback(
    (isPlaying: boolean) => setPlaying(isPlaying),
    []
  )

  if (!data && !audio) {
    return <div>No audio data available.</div>
  }

  const { audioFile, heading, copy, series, coverArt } = (data || audio) ?? {}

  const foregroundColour = ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
    ? strongColour
      ? 'chartreuse'
      : 'white'
    : strongColour
      ? 'mid-blue'
      : 'ink-blue'

  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading audio.</div>
  return (
    <>
      <AudioSingle
        music={audioFile?.sys.uri}
        artwork={
          (isDevelopment && !coverArt?.asset.sys.uri?.startsWith('https')
            ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
            : '') + coverArt?.asset.sys.uri
        }
        showArtwork={true}
        title={heading}
        tag={showSeriesName ? series?.entryTitle : ''}
        description={copy}
        colour={foregroundColour || 'chartreuse'}
        align='top'
        playing={playing}
        onFinish={handleTrackFinish}
        onPlayPause={handlePlayPause}
        isStandalone={true}
      >
        {heading && !copy && <h2 className='font-bold'>{heading}</h2>}
        {heading && copy && (
          <GeneralAccordion
            content={{
              backgroundColour: 'default',
              contentSingleAccordion: [
                {
                  heading: heading,
                  copy: copy
                }
              ]
            }}
            className='w-full !max-w-none'
          />
        )}
      </AudioSingle>
    </>
  )
}

export default AudioStandalone
